import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { resumeTemplates, ResumeTemplate } from '../../mocks/resumeTemplates';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import SortDropdown, { SortOption } from './components/SortDropdown';
import PreviewModal from './components/PreviewModal';
import FavoritesPanel from './components/FavoritesPanel';
import CompareModal from './components/CompareModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedColorTheme, setSelectedColorTheme] = useState('All Colors');
  const [attachedFiles, setAttachedFiles] = useState<Array<{ file: File; preview?: string; id: string }>>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showLogin, setShowLogin] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // "Use Template" selected state
  const [activeTemplate, setActiveTemplate] = useState<ResumeTemplate | null>(null);
  const [showStartBanner, setShowStartBanner] = useState(false);

  // New feature states
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [previewTemplate, setPreviewTemplate] = useState<ResumeTemplate | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);

  // Compare mode states
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // Recently Viewed state
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);

  const { exhausted: trialExhausted, consume: consumeTrial, remaining: trialRemaining, dots: trialDots, used: trialUsed } = useFreeTrialGate('resume-builder');
  const [showUpgradeTrial, setShowUpgradeTrial] = useState(false);

  const categories = [
    'all',
    'Professional & Corporate',
    'Technology & Engineering',
    'Creative & Design',
    'Healthcare & Medical',
    'Finance & Accounting',
    'Education & Academia',
    'Marketing & Communications',
    'Legal & Compliance',
    'Sales & Business Development',
    'Human Resources',
    'Entry Level & Students',
    'Hospitality & Tourism',
    'Science & Research',
  ];

  const toggleFavorite = (name: string) => {
    setFavorites((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const toggleCompareSelection = (name: string) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= 3) return prev;
      return [...prev, name];
    });
  };

  const clearCompareSelection = () => setSelectedForCompare([]);
  const exitCompareMode = () => { setCompareMode(false); setSelectedForCompare([]); };
  const openCompareModal = () => { if (selectedForCompare.length >= 2) setShowCompareModal(true); };

  const trackTemplateView = (templateName: string) => {
    setRecentlyViewed((prev) => {
      // Remove if already exists (to move to front)
      const filtered = prev.filter((name) => name !== templateName);
      // Add to front and limit to 6
      return [templateName, ...filtered].slice(0, 6);
    });
  };

  const clearRecentlyViewed = () => setRecentlyViewed([]);

  // Handle "Use Template" — gate with free trial
  const handleUseTemplate = (template: ResumeTemplate) => {
    if (trialExhausted) {
      setShowUpgradeTrial(true);
      return;
    }
    consumeTrial();
    navigate('/resume-editor', { state: { template } });
  };

  const favoriteTemplates = resumeTemplates.filter((t) => favorites.includes(t.name));
  const compareTemplates = resumeTemplates.filter((t) => selectedForCompare.includes(t.name));
  const recentlyViewedTemplates = recentlyViewed
    .map((name) => resumeTemplates.find((t) => t.name === name))
    .filter((t): t is ResumeTemplate => t !== undefined);

  const sortTemplates = (templates: ResumeTemplate[]): ResumeTemplate[] => {
    switch (sortBy) {
      case 'newest':
        return [...templates].sort(
          (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
        );
      case 'popular':
        return [...templates].sort((a, b) => {
          const order = { Popular: 0, New: 1, null: 2 };
          return (order[a.badge as keyof typeof order] ?? 2) - (order[b.badge as keyof typeof order] ?? 2);
        });
      case 'pages-asc':
        return [...templates].sort((a, b) => a.pages - b.pages);
      case 'pages-desc':
        return [...templates].sort((a, b) => b.pages - a.pages);
      default:
        return templates;
    }
  };

  // Color theme mapping function
  const getColorTheme = (hexColor: string): string => {
    const color = hexColor.toLowerCase();
    
    // Dark theme: navy, charcoal, black, dark grays
    if (['#1e293b', '#334155', '#475569', '#0f172a', '#1a1a1a', '#2d3748', '#1f2937'].includes(color)) {
      return 'Dark';
    }
    
    // Warm theme: red, orange, brown, gold
    if (['#dc2626', '#ea580c', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f'].includes(color)) {
      return 'Warm';
    }
    
    // Colorful theme: violet, cyan, teal, green, blue
    if (['#7c3aed', '#06b6d4', '#14b8a6', '#10b981', '#3b82f6', '#8b5cf6', '#6366f1'].includes(color)) {
      return 'Colorful';
    }
    
    // Minimal theme: light gray, white, soft tones
    if (['#e5e7eb', '#f3f4f6', '#d1d5db', '#9ca3af', '#6b7280'].includes(color)) {
      return 'Minimal';
    }
    
    // Default fallback based on color characteristics
    const rgb = parseInt(color.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    const brightness = (r + g + b) / 3;
    
    if (brightness < 80) return 'Dark';
    if (brightness > 200) return 'Minimal';
    if (r > g && r > b) return 'Warm';
    return 'Colorful';
  };

  const filteredTemplates = sortTemplates(
    resumeTemplates.filter((template) => {
      const matchesSearch =
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' || template.category === selectedCategory;
      const matchesColorTheme =
        selectedColorTheme === 'All Colors' || getColorTheme(template.accentColor) === selectedColorTheme;
      return matchesSearch && matchesCategory && matchesColorTheme;
    })
  );

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter((file) => {
      if (file.size > 25 * 1024 * 1024) { alert(`${file.name} is too large. Max 25MB per file.`); return false; }
      const validTypes = ['image/jpeg','image/png','image/gif','image/webp','video/mp4','video/quicktime','video/x-msvideo','video/webm','application/pdf','application/vnd.openxmlformats-officedocument.wordprocessingml.document','text/plain','text/csv'];
      if (!validTypes.includes(file.type)) { alert(`${file.name} is not a supported file type.`); return false; }
      return true;
    });
    if (attachedFiles.length + newFiles.length > 10) { alert('Maximum 10 attachments allowed.'); return; }
    const filesWithPreview = newFiles.map((file) => {
      const id = Math.random().toString(36).substr(2, 9);
      if (file.type.startsWith('image/')) { const preview = URL.createObjectURL(file); return { file, preview, id }; }
      return { file, id };
    });
    setAttachedFiles((prev) => [...prev, ...filesWithPreview]);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); };

  const removeFile = (id: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('video/')) return 'ri-video-line';
    if (type === 'application/pdf') return 'ri-file-pdf-line';
    if (type.includes('word')) return 'ri-file-word-line';
    if (type === 'text/csv') return 'ri-file-excel-line';
    return 'ri-file-text-line';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePreviewClick = (template: ResumeTemplate) => {
    trackTemplateView(template.name);
    setPreviewTemplate(template);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <i className="ri-cpu-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
                    openclaw.ai
                  </div>
                  <div className="text-xs text-slate-500 -mt-1">Resume Builder</div>
                </div>
              </button>
              
              {/* OpenClaw Algorithm Badge */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 border border-emerald-200 rounded-full">
                <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  OpenClaw Algorithm
                </span>
              </div>
              
              {/* Capability Badge */}
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full">
                <i className="ri-file-list-3-line text-emerald-600 text-sm"></i>
                <span className="text-xs font-medium text-slate-700">10M+ Resumes Generated</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <ToolsDropdown isDarkMode={false} />
              {/* Trial counter */}
              {!trialExhausted ? (
                <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full">
                  <div className="flex gap-1">
                    {trialDots.map((d, i) => (
                      <div key={i} className={`w-1.5 h-1.5 rounded-full ${d.active ? 'bg-gray-300' : 'bg-emerald-500'}`} />
                    ))}
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-700">{trialRemaining} free use{trialRemaining !== 1 ? 's' : ''}</span>
                </div>
              ) : (
                <button onClick={() => setShowUpgradeTrial(true)} className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap">
                  <i className="ri-sparkling-2-fill text-xs"></i>Upgrade
                </button>
              )}
              {/* Favorites button */}
              <button
                onClick={() => setShowFavorites(true)}
                className="relative flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-bookmark-line"></i>
                Saved
                {favorites.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowLogin(true)}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <span className="text-sm font-semibold">Powered by OpenClaw Algorithm</span>
            </div>
            <div className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <span className="text-sm font-semibold">ATS-Optimized by OpenClaw</span>
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-4">Professional Resume Builder</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Create ATS-optimized resumes powered by OpenClaw AI. Choose from professionally designed templates and land your dream job.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search and Filters */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`bg-white rounded-2xl border-2 p-6 mb-8 shadow-sm transition-all ${
            isDragging ? 'border-emerald-500 shadow-emerald-200' : 'border-gray-200'
          }`}
        >
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search resume templates..."
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <button
              onClick={() => setShowAttachments(!showAttachments)}
              className={`px-5 py-3 rounded-xl font-medium transition-all whitespace-nowrap cursor-pointer ${
                attachedFiles.length > 0
                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-attachment-2 mr-2"></i>
              {attachedFiles.length > 0 ? `${attachedFiles.length} Files` : 'Attach'}
            </button>
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category === 'all' ? 'All' : category}
              </button>
            ))}
          </div>

          {/* Color Theme Filter */}
          <div className="flex items-center gap-3 mb-6 overflow-x-auto pb-2 scrollbar-hide">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Color Theme:</span>
            
            <button
              onClick={() => setSelectedColorTheme('All Colors')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedColorTheme === 'All Colors'
                  ? 'bg-slate-800 text-white shadow-lg'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              All Colors
            </button>

            <button
              onClick={() => setSelectedColorTheme('Dark')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedColorTheme === 'Dark'
                  ? 'bg-slate-800 text-white shadow-lg ring-2 ring-slate-800 ring-offset-2'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <div className="w-4 h-4 rounded-full bg-slate-800 border border-slate-300"></div>
              Dark
            </button>

            <button
              onClick={() => setSelectedColorTheme('Warm')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedColorTheme === 'Warm'
                  ? 'bg-orange-600 text-white shadow-lg ring-2 ring-orange-600 ring-offset-2'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-orange-500 to-red-600 border border-slate-300"></div>
              Warm
            </button>

            <button
              onClick={() => setSelectedColorTheme('Colorful')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedColorTheme === 'Colorful'
                  ? 'bg-gradient-to-r from-violet-500 to-cyan-600 text-white shadow-lg ring-2 ring-violet-500 ring-offset-2'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-violet-500 via-blue-500 to-cyan-500 border border-slate-300"></div>
              Colorful
            </button>

            <button
              onClick={() => setSelectedColorTheme('Minimal')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedColorTheme === 'Minimal'
                  ? 'bg-slate-300 text-slate-800 shadow-lg ring-2 ring-slate-300 ring-offset-2'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <div className="w-4 h-4 rounded-full bg-slate-200 border border-slate-300"></div>
              Minimal
            </button>
          </div>

          {/* Attachments Panel */}
          {showAttachments && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Attachments {attachedFiles.length > 0 && `(${attachedFiles.length}/10)`}
                </label>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  + Add Files
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.docx,.txt,.csv"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              {attachedFiles.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-gray-50">
                  <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-3"></i>
                  <p className="text-sm text-gray-600 mb-1">Drag &amp; drop files here or click &quot;Add Files&quot;</p>
                  <p className="text-xs text-gray-500">Upload your existing resume, portfolio, or reference documents</p>
                  <p className="text-xs text-gray-500 mt-1">Images, Videos, PDF, DOCX, TXT, CSV &bull; Max 25MB per file &bull; Max 10 files</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {attachedFiles.some((f) => f.preview) && (
                    <div className="grid grid-cols-6 gap-3">
                      {attachedFiles.filter((f) => f.preview).map(({ file, preview, id }) => (
                        <div key={id} className="relative group">
                          <img src={preview} alt={file.name} className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                          <button onClick={() => removeFile(id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            <i className="ri-close-line text-sm"></i>
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 rounded-b-lg truncate">{file.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachedFiles.filter((f) => !f.preview).map(({ file, id }) => (
                    <div key={id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg group hover:border-gray-300 transition-colors">
                      <i className={`${getFileIcon(file.type)} text-2xl text-gray-400`}></i>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                      <button onClick={() => removeFile(id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recently Viewed Section */}
        {recentlyViewedTemplates.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-history-line text-xl text-gray-600"></i>
                <h3 className="text-lg font-bold text-gray-900">Recently Viewed</h3>
                <span className="text-sm text-gray-500">({recentlyViewedTemplates.length})</span>
              </div>
              <button
                onClick={clearRecentlyViewed}
                className="text-sm text-gray-500 hover:text-red-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear history
              </button>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {recentlyViewedTemplates.map((template) => (
                <div
                  key={template.name}
                  className="flex-shrink-0 w-48 bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:shadow-lg hover:border-emerald-300 transition-all duration-300 cursor-pointer group"
                >
                  <div className="relative overflow-hidden bg-gray-100" style={{ aspectRatio: '3/4' }}>
                    <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ backgroundColor: template.color }} />
                    <img
                      src={template.image}
                      alt={template.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Quick Preview Button Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-3">
                      <button
                        onClick={() => handlePreviewClick(template)}
                        className="w-full px-3 py-2 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-eye-line mr-1"></i>Preview
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-3">
                    <h4 className="text-xs font-bold text-gray-900 truncate mb-1">{template.name}</h4>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: template.color + '18', color: template.color }}
                      >
                        {template.pages}p
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results bar with Sort and Compare Toggle */}
        {filteredTemplates.length > 0 && (
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-500">
                Showing <span className="font-semibold text-gray-800">{filteredTemplates.length}</span> templates
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <i className="ri-shield-check-line text-emerald-500"></i>
                All templates are ATS-optimized
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (compareMode) {
                    exitCompareMode();
                  } else {
                    setCompareMode(true);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  compareMode
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-file-compare-line"></i>
                {compareMode ? 'Exit Compare' : 'Compare'}
              </button>
              <SortDropdown value={sortBy} onChange={setSortBy} />
            </div>
          </div>
        )}

        {/* Compare Mode Info Banner */}
        {compareMode && (
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-multiple-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Compare Mode Active</p>
                  <p className="text-xs text-gray-600">
                    Select 2-3 templates to compare side by side {selectedForCompare.length > 0 && `• ${selectedForCompare.length} selected`}
                  </p>
                </div>
              </div>
              {selectedForCompare.length > 0 && (
                <button
                  onClick={clearCompareSelection}
                  className="text-sm font-semibold text-violet-600 hover:text-violet-700 whitespace-nowrap cursor-pointer"
                >
                  Clear Selection
                </button>
              )}
            </div>
            {selectedForCompare.length >= 3 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <i className="ri-information-line"></i>
                <span>Maximum 3 templates can be compared at once</span>
              </div>
            )}
          </div>
        )}

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => {
            const isFav = favorites.includes(template.name);
            const isSelected = selectedForCompare.includes(template.name);
            return (
              <div
                key={template.id}
                className={`group bg-white rounded-2xl border-2 border-slate-200 hover:border-emerald-400 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer ${
                  isSelected ? 'border-violet-500 shadow-lg shadow-violet-200' : 'border-gray-200'
                }`}
              >
                {/* Compare Mode Checkbox */}
                {compareMode && (
                  <div className="absolute top-3 left-3 z-20">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCompareSelection(template.name);
                      }}
                      className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-violet-500 border-violet-500 text-white'
                          : 'bg-white/90 border-gray-300 hover:border-violet-400 backdrop-blur-sm'
                      }`}
                    >
                      {isSelected && <i className="ri-check-line text-sm font-bold"></i>}
                    </button>
                  </div>
                )}

                {/* Thumbnail */}
                <div className="relative overflow-hidden bg-gray-100" style={{ aspectRatio: '3/4' }}>
                  <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ backgroundColor: template.color }} />
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* Badges */}
                  <div className={`absolute top-3 z-10 flex flex-col gap-1.5 ${compareMode ? 'left-12' : 'left-3'}`}>
                    {template.badge === 'Popular' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow-md">
                        🔥 Popular
                      </span>
                    )}
                    {template.badge === 'New' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500 text-white shadow-md animate-pulse">
                        ✨ New
                      </span>
                    )}
                  </div>

                  {/* Pages badge + Bookmark */}
                  <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5">
                    <span className="px-2 py-0.5 bg-black/60 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                      {template.pages}p
                    </span>
                    {!compareMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(template.name); }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full backdrop-blur-sm transition-all cursor-pointer ${
                          isFav
                            ? 'bg-amber-500 text-white shadow-md'
                            : 'bg-black/50 text-white hover:bg-amber-500'
                        }`}
                        title={isFav ? 'Remove from saved' : 'Save template'}
                      >
                        <i className={`${isFav ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-xs`}></i>
                      </button>
                    )}
                  </div>

                  {/* Hover overlay */}
                  {!compareMode && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 gap-2">
                      <button
                        onClick={() => handlePreviewClick(template)}
                        className="w-full px-3 py-2 bg-white text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-eye-line mr-1.5"></i>Preview
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUseTemplate(template); }}
                        className="w-full px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all whitespace-nowrap cursor-pointer hover:opacity-90"
                        style={{ backgroundColor: template.color }}
                      >
                        <i className="ri-edit-line mr-1.5"></i>Use Template
                      </button>
                    </div>
                  )}
                </div>

                {/* Card Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-bold text-gray-900 leading-tight">{template.name}</h3>
                  </div>
                  <p className="text-xs text-gray-400 mb-3 line-clamp-2 leading-relaxed">{template.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: template.color + '18', color: template.color }}
                    >
                      {template.category.split(' ')[0]}
                    </span>
                    {!compareMode && (
                      <button
                        onClick={() => handlePreviewClick(template)}
                        className="text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
                        style={{ color: template.color }}
                      >
                        Preview <i className="ri-arrow-right-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-search-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Sticky Bottom Bar for Compare Mode */}
      {compareMode && selectedForCompare.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-violet-200 shadow-2xl">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{selectedForCompare.length}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      {selectedForCompare.length} template{selectedForCompare.length > 1 ? 's' : ''} selected
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedForCompare.length < 2 ? 'Select at least 2 to compare' : 'Ready to compare'}
                    </p>
                  </div>
                </div>

                {/* Selected Template Thumbnails */}
                <div className="flex gap-2 ml-4">
                  {compareTemplates.map((template) => (
                    <div key={template.name} className="relative group">
                      <div className="w-16 h-20 rounded-lg overflow-hidden border-2 border-violet-300 shadow-md">
                        <img
                          src={template.image}
                          alt={template.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <button
                        onClick={() => toggleCompareSelection(template.name)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 text-center truncate">
                        {template.name.split(' ')[0]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={clearCompareSelection}
                  className="px-5 py-2.5 rounded-xl font-semibold text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  Clear
                </button>
                <button
                  onClick={openCompareModal}
                  disabled={selectedForCompare.length < 2}
                  className={`px-6 py-2.5 rounded-xl font-bold text-sm text-white transition-all whitespace-nowrap cursor-pointer ${
                    selectedForCompare.length >= 2
                      ? 'bg-gradient-to-r from-violet-500 to-purple-600 hover:shadow-lg'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-file-compare-line mr-2"></i>
                  Compare Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          isFavorited={favorites.includes(previewTemplate.name)}
          onToggleFavorite={() => toggleFavorite(previewTemplate.name)}
          onClose={() => setPreviewTemplate(null)}
          onUse={() => handleUseTemplate(previewTemplate)}
        />
      )}

      {/* Favorites Panel */}
      {showFavorites && (
        <FavoritesPanel
          favorites={favoriteTemplates}
          onRemove={(name) => toggleFavorite(name)}
          onPreview={(t) => { setShowFavorites(false); handlePreviewClick(t); }}
          onClose={() => setShowFavorites(false)}
        />
      )}

      {/* Compare Modal */}
      {showCompareModal && (
        <CompareModal
          templates={compareTemplates}
          onClose={() => setShowCompareModal(false)}
          onUseTemplate={(template) => {
            setShowCompareModal(false);
            handleUseTemplate(template);
          }}
        />
      )}

      {/* Login Modal */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showUpgradeTrial && (
        <UpgradeTrialModal
          toolName="Resume Builder"
          usedCount={trialUsed}
          onSignIn={() => { setShowUpgradeTrial(false); setShowLogin(true); }}
          onClose={() => setShowUpgradeTrial(false)}
        />
      )}
    </div>
  );
}